import { useQueryClient } from '@tanstack/react-query';
import { Badge, Grid, Typography } from '@kc-console/common-ui';

import { announcementQueries } from '../api/announcement';
import { notificationQueries } from '../api/notification';
import { NotificationCategory } from '../constants/types/notification';
import { StreamCategory } from '../constants/types/stream';
import { useNotificationPreferenceStore } from '../stores';
import {
  getNotificationCountLabels,
  getNotificationDisplayMeta,
  type NotificationCountLabel,
} from '../utils/notificationDisplay';
import showWebNotification from '../utils/showWebNotification';
import { useNotificationToast } from './useNotificationToast';
import useStreamChannel from './useStreamChannel';

import type { Notification } from '../constants/types/notification';

interface UseNotificationWithSSEOptions {
  enabled?: boolean;
  onNotificationToastClick?: (notification: Notification) => void;
}

interface NotificationToastSubTitleProps {
  notification: Notification;
}

interface NotificationToastLabelProps {
  countLabels: NotificationCountLabel[];
}

const NotificationToastSubTitle = ({ notification }: NotificationToastSubTitleProps) => {
  const { categoryLabel, time } = getNotificationDisplayMeta(notification);

  return (
    <Grid container alignItems="center" gap={'4px'}>
      {categoryLabel && (
        <Typography variant="label/medium/2xs" color="text/weaker/default">
          {categoryLabel}
        </Typography>
      )}
      {categoryLabel && time && (
        <Typography variant="label/regular/2xs" color="text/weakest/default">
          ·
        </Typography>
      )}
      {time && (
        <Typography variant="label/regular/2xs" color="text/weakest/default">
          {time}
        </Typography>
      )}
    </Grid>
  );
};

const NotificationToastLabel = ({ countLabels }: NotificationToastLabelProps) => {
  if (countLabels.length === 0) return null;

  return (
    <Grid container alignItems="center" gap={0.5}>
      {countLabels.map(countLabel => (
        <Badge
          key={countLabel.key}
          color={countLabel.color}
          variant="tint"
          shape="circular"
          label={countLabel.label}
        />
      ))}
    </Grid>
  );
};

/**
 * SSE NOTIFICATION 이벤트 도착 시 notification query를 자동 invalidate.
 * @description
 * - SSE 이벤트 도착 시 `notificationQueries.all()` 을 invalidate 해서
 *   badge 뿐 아니라 popper 의 feed 까지 함께 refetch.
 * - 인앱 push 설정이 켜져 있으면 Notification toast를 노출.
 * - useStreamChannel 자체에 3초 batcher 가 있어 burst 는 자연스럽게 흡수됨.
 */
const useNotificationWithSSE = (options: UseNotificationWithSSEOptions = {}) => {
  const { enabled = true, onNotificationToastClick } = options;
  const queryClient = useQueryClient();
  const { dismissNotification, enqueueNotification } = useNotificationToast();
  const inAppPushEnabled = useNotificationPreferenceStore(state => state.inAppPushEnabled);
  const browserPushEnabled = useNotificationPreferenceStore(state => state.browserPushEnabled);

  useStreamChannel({
    onMessageFlush: events => {
      if (!enabled) return;

      const hasNotification = events.some(event => event.category === StreamCategory.NOTIFICATION);
      if (!hasNotification) return;

      queryClient.invalidateQueries({ queryKey: notificationQueries.all() });

      const notificationEvents = events.filter(
        event => event.category === StreamCategory.NOTIFICATION,
      );

      const hasPlatformNotice = notificationEvents.some(
        event => event.payload.category === NotificationCategory.PLATFORM_NOTICE,
      );

      const pushEvents = notificationEvents.filter(event => !!event.payload.severity); // 추후 조건이 있을 경우 추가 가능하도록 filter

      // invalidate 는 active 구독자(대시보드 useRefetchOnAnnouncementChange, 열려 있는 새소식 상세 모달)를 refetch 시켜
      // 목록·본문을 갱신하고, 대시보드 위젯은 dataUpdatedAt 갱신을 감지해 자체 쿼리를 다시 요청한다.
      if (hasPlatformNotice) {
        queryClient.invalidateQueries({
          queryKey: announcementQueries.all(),
        });
      }

      if (browserPushEnabled) {
        pushEvents.forEach(event => {
          showWebNotification({
            id: event.payload.id,
            title: event.payload.title,
            description: event.payload.description,
            // OS 웹 푸시 클릭 → 인앱 토스트 클릭과 동일 효과(popper 열기 + 읽음).
            // OS 알림 자체는 showWebNotification의 onclick(notification.close)이 닫는다.
            onClick: onNotificationToastClick
              ? () => onNotificationToastClick(event.payload)
              : undefined,
          });
        });
      }

      if (inAppPushEnabled) {
        pushEvents.forEach(event => {
          const countLabels = getNotificationCountLabels(event.payload);

          enqueueNotification(
            {
              subTitle: <NotificationToastSubTitle notification={event.payload} />,
              title: event.payload.title,
              label:
                countLabels.length > 0 ? (
                  <NotificationToastLabel countLabels={countLabels} />
                ) : undefined,
              description: event.payload.description,
              variant: event.payload.severity,
              onClick: onNotificationToastClick
                ? () => {
                    onNotificationToastClick(event.payload);
                    dismissNotification(event.payload.id);
                  }
                : undefined,
            },
            { id: event.payload.id, duration: 3000 },
          );
        });
      }
    },
  });
};

export default useNotificationWithSSE;
